import { useEffect, useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControl,
  FormHelperText,
  Stack,
  Paper,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "../../../config/axios";
import CommonDialog from "../../../components/CommonDialog";

const AddLanguageModal = (props) => {
  // modal states
  const [languageData, setLanguageData] = useState({});
  const [validations, setValidations] = useState({
    language: { status: false, msg: "" },
  });
  const [newLanguage, setNewLanguage] = useState("");
  const [alertMsg, setAlertMsg] = useState({});

  // Reset state when modal opens
  useEffect(() => {
    if (props.open?.addLanguage) {
      setLanguageData({});
      setValidations({ language: { status: false, msg: "" } });
      setNewLanguage("");
    }
  }, [props.open?.addLanguage]);

  // other functions
  const handleSubmit = () => {
    if (isFormValid()) {
      props.setEmployeeData((prevState) => {
        return {
          ...prevState,
          languages: [...(prevState.languages || []), languageData],
        };
      });
      props.setOpen({ ...props.open, addLanguage: false });
      setLanguageData({});
    }
  };

  const isFormValid = () => {
    // Must have a selected language id
    if (!languageData?.id || !String(languageData.id).length) {
      setValidations(() => ({
        language: { status: true, msg: "Required!" },
      }));
      return false;
    }

    // Check if already exists in employee's language list
    const existingIds = (props.employeeData?.languages || []).map((lang) =>
      String(lang.id || lang._id || "")
    );
    if (existingIds.includes(String(languageData.id))) {
      setValidations(() => ({
        language: { status: true, msg: "Already Exists" },
      }));
      return false;
    }
    return true;
  };

  const addLanguage = () => {
    if (!newLanguage.trim()) {
      setValidations((prevState) => ({
        ...prevState,
        newLanguage: { status: true, msg: "Required!" },
      }));
      return;
    }

    const body = { name: newLanguage.trim() };
    axios
      .post("/admin/languages", body)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            setAlertMsg({
              status: true,
              msg: "Language added successfully!",
              severity: "success",
            });
            setValidations((prev) => ({ ...prev, newLanguage: { status: false, msg: "" } }));
            setNewLanguage("");
            props.fetchLanguageOptions();
          } else {
            if (res.data.type === "validation" && res.data.error) {
              const tempValidations = {};
              res.data.error.forEach((val) => {
                tempValidations.newLanguage = {
                  status: true,
                  msg: val.message,
                };
              });
              setValidations((prevState) => ({
                ...prevState,
                ...tempValidations,
              }));
            }
          }
        }
      })
      .catch((err) => console.log(err));
  };

  return (
    <>
      <CommonDialog
        open={props.open.addLanguage}
        title={"Add Language"}
        onSubmit={handleSubmit}
        onClose={() => {
          props.setOpen({ ...props.open, addLanguage: false });
          setLanguageData({});
          setValidations({ language: { status: false, msg: "" } });
          setNewLanguage("");
        }}
      >
        <Paper style={{ ...props.style, width: "100%" }} elevation={0}>
          <Box sx={{ padding: "10px" }}>
            <Stack spacing={1}>
              <Grid container spacing={1}>
                <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
                  <Grid container spacing={1}>
                    <Grid xs={9} sm={9} md={9} lg={9} xl={10} item>
                      <TextField
                        size="small"
                        placeholder={"Add new language name"}
                        value={newLanguage}
                        onChange={(e) => {
                          setNewLanguage(e.target.value);
                          if (validations.newLanguage?.status) {
                            setValidations((prev) => ({
                              ...prev,
                              newLanguage: { status: false, msg: "" },
                            }));
                          }
                        }}
                        error={validations.newLanguage?.status}
                        helperText={validations.newLanguage?.msg || ""}
                        fullWidth
                      />
                    </Grid>
                    <Grid xs={3} sm={3} md={3} lg={3} xl={2} item>
                      <Button
                        variant="contained"
                        onClick={addLanguage}
                        sx={{ width: "100%" }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Select from existing languages */}
                <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
                  <FormControl
                    error={validations.language?.status}
                    sx={{ width: "100%" }}
                  >
                    <Select
                      size="small"
                      value={languageData.id || ""}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const opt = props.activeLanguages.find(
                          (lang) => String(lang.id) === String(selectedId)
                        );
                        if (opt) {
                          setLanguageData({ id: opt.id, name: opt.name, value: 3 });
                          // Clear validation error on valid selection
                          setValidations((prev) => ({
                            ...prev,
                            language: { status: false, msg: "" },
                          }));
                        }
                      }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: "#919fa5" }}>Select Language</span>;
                        }
                        const found = props.activeLanguages?.find(
                          (opt) => String(opt.id) === String(selected)
                        );
                        return found?.name || <span style={{ color: "#919fa5" }}>Select Language</span>;
                      }}
                    >
                      {props.activeLanguages?.map((lang) => (
                        <MenuItem key={String(lang.id)} value={String(lang.id)}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {validations.language?.status && (
                      <FormHelperText>
                        {validations.language?.msg}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Paper>
      </CommonDialog>

      <Snackbar
        open={alertMsg.status}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={2000}
        onClose={() => setAlertMsg({})}
      >
        <Alert severity={alertMsg.severity || "success"} sx={{ width: "100%" }}>
          {alertMsg.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddLanguageModal;